<template>
    <v-dialog :value="value" max-width="450px" persistent @keydown.esc="close" @click:outside="close" aria-label="Pause or resume project">
        <v-card id="pauseProjectDialog">
            <v-card-title>
                <span class="mr-2" style="text-transform: uppercase; font-weight: 100; font-size: 16px">{{ paused ? "Resume Project" : "Pause Project" }}</span>
                <v-btn @click="close" :disabled="loading" right top absolute icon color="primary" aria-label="Close dialog"><v-icon>mdi-close</v-icon></v-btn>
            </v-card-title>
            <v-card-subtitle>
                <v-chip class="mt-1" small outlined color="primary"> {{ project.title }}</v-chip>
            </v-card-subtitle>
            <v-card-text class="mt-2">
                <p v-if="!paused" class="mb-3">
                    While a project is paused, no updates are fetched. All data collected so far stays available. You can still add {{ isAi ? "prompts or platforms" : "keywords" }} and
                    change the interval - they only use credits once you resume the project.
                </p>
                <p v-else class="mb-3">
                    Updates start again with the next run, including everything added while the project was paused. Days missed during the pause are not fetched
                    retroactively.
                </p>
                <div v-if="!project.demo_project && usage.total > 0" class="qgreycard my-0">
                    <v-icon small class="mr-1">mdi-database-outline</v-icon>
                    <span v-if="!paused">
                        Releases <b>{{ usage.total }}</b> {{ usage.unit }} per month. The credits become available again after the current update cycle.
                    </span>
                    <span v-else>
                        Reserves <b>{{ usage.total }}</b> {{ usage.unit }} per month ({{ intervalText }}, current number of {{ isAi ? "prompts and platforms" : "keywords" }}).
                    </span>
                </div>
                <div class="d-flex">
                    <v-chip class="my-2 mx-auto py-1" style="white-space: normal; height: auto" v-if="error" color="error"><v-icon class="mr-1">mdi-alert</v-icon>{{ error }}</v-chip>
                </div>
            </v-card-text>
            <v-card-actions class="px-auto">
                <v-btn class="mr-3" text rounded :disabled="loading" @click="close"> Cancel </v-btn>
                <v-btn :loading="loading" rounded class="ml-auto" color="primary" @click="confirm">
                    <v-icon left small>{{ paused ? "mdi-play" : "mdi-pause" }}</v-icon>
                    {{ paused ? "resume" : "pause" }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
    import {
        projectCreditUsage,
        intervalLabel,
        isProjectPaused,
        setProjectPaused,
        buildNotEnoughCreditsMessage,
        importInProgressMessage,
        fetchEntryCount,
        withEntryCount
    } from "../helper/projectStatus"

    // Bestätigt Pausieren bzw. Fortsetzen eines Monitoring- oder AI-Suite-Projekts, zeigt die
    // Auswirkung auf die Credits und führt den Aufruf selbst aus. Eltern bekommen:
    //   changed(paused: Boolean)          -> Projekt lokal aktualisieren
    //   notify(message, icon, color)      -> eigene Benachrichtigung anzeigen
    export default {
        name: "PauseProjectDialog",
        props: {
            value: { type: Boolean, default: false },
            project: { type: Object, required: true }
        },
        data() {
            // entryCount: aktuelle Keyword-/Prompt-Zahl vom Server, beim Öffnen geladen (null bis dahin)
            return { loading: false, error: null, entryCount: null }
        },
        computed: {
            paused() {
                return isProjectPaused(this.project)
            },
            // Plattformen gibt es nur in der AI Suite
            isAi() {
                return this.project.type === "ai_tracking"
            },
            usage() {
                return projectCreditUsage(withEntryCount(this.project, this.entryCount))
            },
            intervalText() {
                return intervalLabel(this.project.interval)
            }
        },
        watch: {
            value: {
                immediate: true,
                async handler(val) {
                    if (!val) return
                    this.error = null
                    this.entryCount = null
                    this.entryCount = await fetchEntryCount({ projectId: this.project._id, axiosPost: this.$helpers.axiosPost.bind(this.$helpers), cryptoJS: this.CryptoJS })
                }
            }
        },
        methods: {
            close() {
                if (this.loading) return
                this.$emit("input", false)
            },
            async confirm() {
                this.loading = true
                this.error = null
                const targetPaused = !this.paused
                try {
                    const result = await setProjectPaused({
                        projectId: this.project._id,
                        paused: targetPaused,
                        axiosPost: this.$helpers.axiosPost.bind(this.$helpers),
                        cryptoJS: this.CryptoJS
                    })
                    if (result.error === "not_enough_credits") {
                        this.error = buildNotEnoughCreditsMessage({ requested: result.required, unit: this.usage.unit, responseData: result })
                        return
                    }
                    if (result.error === "import_in_progress") {
                        this.error = importInProgressMessage(result.import_type)
                        return
                    }
                    if (result.error) {
                        this.error = "Something went wrong. Please try again or contact the support!"
                        return
                    }
                    this.$emit("changed", targetPaused)
                    this.$emit("notify", targetPaused ? "Project paused" : "Project resumed. Updates start with the next run.", "mdi-check-circle", "primary")
                    this.$emit("input", false)
                } finally {
                    this.loading = false
                }
            }
        }
    }
</script>

<style scoped>
    #pauseProjectDialog .qgreycard {
        font-size: 13px;
    }
</style>
